import { useMemo } from 'react';

import { useChatMessageSender, type UseChatMessageSenderOptions } from './useChatMessageSender';
import { useChatStreamConnection } from './useChatStreamConnection';
import { useChatStreamState } from './useChatStreamState';

export type ChatStreamMessageSenderConfig = Partial<
  Pick<UseChatMessageSenderOptions, 'encodeFile' | 'buildRequestPayload' | 'buildUserMessage'>
>;

export interface UseChatStreamOptions {
  ensureSession: () => Promise<boolean>;
  authenticatedFetch: (url: string, options?: RequestInit) => Promise<Response>;
  buildStreamUrl: (conversationId: string) => string;
  buildExecuteUrl: (conversationId: string) => string;
  messageSenderConfig?: ChatStreamMessageSenderConfig;
}

export const useChatStream = ({
  ensureSession,
  authenticatedFetch,
  buildStreamUrl,
  buildExecuteUrl,
  messageSenderConfig,
}: UseChatStreamOptions) => {
  const {
    messages,
    recentEvents,
    progressState,
    connectionStatus,
    currentRunId,
    setMessages,
    setConnectionStatus,
    resetConversationState,
    handleAgUiEvent,
  } = useChatStreamState();

  const { connectToConversation, disconnect, activeConversationRef } = useChatStreamConnection({
    ensureSession,
    buildStreamUrl,
    onEvent: handleAgUiEvent,
    onStatusChange: setConnectionStatus,
    onResetState: resetConversationState,
  });

  const { sendMessage } = useChatMessageSender({
    authenticatedFetch,
    buildExecuteUrl,
    getConversationId: () => activeConversationRef.current,
    getConnectionStatus: () => connectionStatus,
    setMessages,
    ...(messageSenderConfig?.encodeFile ? { encodeFile: messageSenderConfig.encodeFile } : {}),
    ...(messageSenderConfig?.buildRequestPayload
      ? { buildRequestPayload: messageSenderConfig.buildRequestPayload }
      : {}),
    ...(messageSenderConfig?.buildUserMessage
      ? { buildUserMessage: messageSenderConfig.buildUserMessage }
      : {}),
  });

  const isConnected = useMemo(() => connectionStatus === 'connected', [connectionStatus]);

  return {
    messages,
    recentEvents,
    progressState,
    connectionStatus,
    currentRunId,
    connectToConversation,
    disconnect,
    resetConversationState,
    sendMessage,
    isConnected,
  };
};
